import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

var avatarPalettes = [
  { background: '#ddeeff', color: '#2255aa' },
  { background: '#ffeedd', color: '#b86010' },
  { background: '#ddf5e8', color: '#1a7a45' },
  { background: '#f5ddff', color: '#7722aa' },
  { background: '#ffd6d6', color: '#cc2222' },
];

function getInitials(name) {
  if (!name) return '?';
  var parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function getAvatarStyle(name) {
  var idx = name ? name.charCodeAt(0) % avatarPalettes.length : 0;
  return avatarPalettes[idx];
}

function getStatusInfo(status) {
  var s = parseInt(status);
  if (s === 4) return { label: 'Restricted', color: '#e67e22', bg: '#fef5ec' };
  if (s === 1) return { label: 'Active', color: '#27ae60', bg: '#eafaf1' };
  if (s === 2) return { label: 'Inactive', color: '#e74c3c', bg: '#fdf0ef' };
  return { label: 'Active', color: '#27ae60', bg: '#eafaf1' };
}

function getButtonStyle(variant) {
  var base = {
    padding: '8px 14px',
    fontSize: '12px',
    fontWeight: '600',
    borderRadius: '6px',
    cursor: 'pointer',
    border: '1px solid transparent',
    whiteSpace: 'nowrap',
  };
  if (variant === 'primary') return Object.assign({}, base, { background: '#d5a67b', color: '#fff', borderColor: '#d5a67b' });
  if (variant === 'danger') return Object.assign({}, base, { background: '#fff', color: '#e74c3c', borderColor: '#e74c3c' });
  if (variant === 'success') return Object.assign({}, base, { background: '#27ae60', color: '#fff', borderColor: '#27ae60' });
  if (variant === 'warning') return Object.assign({}, base, { background: '#e67e22', color: '#fff', borderColor: '#e67e22' });
  return Object.assign({}, base, { background: '#f5f0eb', color: '#485257', borderColor: '#e8ddd6' });
}

function InfoRow(props) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid #f0ebe5', gap: '12px' }}>
      <span style={{ fontSize: '12px', color: '#999', fontWeight: '500', flexShrink: 0, minWidth: '120px' }}>{props.label}</span>
      <span style={{ fontSize: '13px', color: '#485257', fontWeight: '500', textAlign: 'right', wordBreak: 'break-word' }}>{props.value || '-'}</span>
    </div>
  );
}

export default function ClientInfoPanel(props) {
  var client = props.client;
  var details = props.details;
  var detailsLoading = props.detailsLoading;
  var onClose = props.onClose;
  var actions = props.actions || [];

  var isOpen = client ? true : false;
  useEffect(function() {
    if (!isOpen) {
      document.documentElement.style.overflow = '';
      return;
    }
    document.documentElement.style.overflow = 'hidden';
    return function() {
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  if (!client) return null;

  var displayName = client.screen_name || ((client.first_name || '') + ' ' + (client.last_name || '')).trim() || 'N/A';
  var avatarStyle = getAvatarStyle(displayName);
  var status = getStatusInfo(client.status);

  var general = details && details.general ? details.general : null;

  return ReactDOM.createPortal(
    <div>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 9998,
        }}
      />

      {/* Slide-in panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100%',
          width: window.innerWidth <= 600 ? '100vw' : '360px',
          maxWidth: '100vw',
          background: '#fff',
          zIndex: 9999,
          boxShadow: '-6px 0 32px rgba(0,0,0,0.14)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Header */}
        <div style={{ background: '#f8f4f0', padding: '18px 20px 14px', borderBottom: '1px solid #e8ddd6', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={Object.assign({}, avatarStyle, {
              width: 46,
              height: 46,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700',
              fontSize: '17px',
              flexShrink: 0,
            })}>
              {getInitials(displayName)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#d5a67b', marginBottom: '2px' }}>
                Client Information
              </div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#485257', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayName}
              </div>
              <div style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {client.email}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '22px', lineHeight: 1, padding: '4px', flexShrink: 0, alignSelf: 'flex-start' }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Info section */}
        <div style={{ padding: '4px 20px 8px', flex: 1 }}>
          {client.first_name && <InfoRow label="Full Name" value={(client.first_name + ' ' + (client.last_name || '')).trim()} />}
          <InfoRow
            label="Status"
            value={
              <span style={{ background: status.bg, color: status.color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                {status.label}
              </span>
            }
          />
          <InfoRow label="Test User" value={client.test_user === 1 ? 'On' : 'Off'} />
          <InfoRow label="Referral Code" value={client.referralCode || '-'} />
          {client.extra_tags && <InfoRow label="Tags" value={client.extra_tags} />}
          {client.created_at && <InfoRow label="Created" value={client.created_at} />}
          {client.volunteer_date && <InfoRow label="Volunteer Date" value={client.volunteer_date} />}
          {general && general.phone && <InfoRow label="Phone" value={general.phone} />}
          {general && general.country && <InfoRow label="Country" value={general.country} />}
          {((general && (general.timezone_label || general.timezone)) || client.timezone_label || client.timezone) && (
            <InfoRow label="Timezone" value={(general && (general.timezone_label || general.timezone)) || client.timezone_label || client.timezone} />
          )}
        </div>

        {/* Action buttons */}
        {actions.length > 0 && (
          <div style={{ padding: '14px 20px 20px', borderTop: '1px solid #f0ebe5', flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {actions.map(function(action, i) {
              return (
                <button key={i} onClick={action.onClick} style={getButtonStyle(action.variant)}>
                  {action.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
